import React, { useMemo, useState } from 'react'
import {
  Box,
  Breadcrumbs,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  Link,
  Chip,
} from '@mui/material'
import {
  NavigateNext as NavigateNextIcon,
  Search as SearchIcon,
  SearchOff as SearchOffIcon,
  AutoAwesomeTwoTone as AutoAwesomeIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material'

import type { MoveToThemeViewProps, Theme } from './Types'
import { truncateText } from 'containers/TextAnalysis/Helpers/ThemesHelper'
import { useMoveToThemeStyles } from '../Hooks/UseThemeStyles'

const MoveToThemeView: React.FC<MoveToThemeViewProps> = ({
  selectedKeyPhrase,
  themeId,
  themes,
  onMove,
  onExitMoveMode,
}) => {
  const [moveSearchTerm, setMoveSearchTerm] = useState('')
  const styles = useMoveToThemeStyles()

  const filteredThemes = useMemo(() => {
    const term = moveSearchTerm.toLowerCase()
    return themes.filter(
      (theme) =>
        theme.id !== themeId &&
        theme.attributes.name.toLowerCase().includes(term),
    )
  }, [themes, themeId, moveSearchTerm])

  return (
    <>
      <Box sx={styles.breadcrumbsContainer}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            color="primary"
            underline="hover"
            onClick={onExitMoveMode}
            sx={styles.backLink}
            tabIndex={0}
            role="button"
            aria-label="Back to key phrases"
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onExitMoveMode()
              }
            }}
          >
            <BackIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            <Typography variant="body2" fontWeight={500}>
              Back
            </Typography>
          </Link>

          <Typography color="text.secondary" variant="body2">
            {truncateText(selectedKeyPhrase, 45)}
          </Typography>
        </Breadcrumbs>
      </Box>

      <TextField
        fullWidth
        placeholder="Search themes..."
        value={moveSearchTerm}
        onChange={(e) => setMoveSearchTerm(e.target.value)}
        variant="outlined"
        autoComplete="off"
        size="small"
        sx={styles.searchBox}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Box sx={styles.destinationHeader}>
        <Typography variant="button" color="text.secondary" fontSize={12}>
          Select destination theme
        </Typography>
        <Chip label={filteredThemes.length} size="small" sx={styles.chip} />
      </Box>

      {filteredThemes.length > 0 ? (
        <List sx={styles.list}>
          {filteredThemes.map((theme: Theme) => (
            <ListItem
              key={theme.id}
              tabIndex={0}
              onClick={() => onMove(theme)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onMove(theme)
                }
              }}
              sx={styles.listItem}
            >
              <ListItemIcon sx={styles.listItemIcon}>
                <AutoAwesomeIcon
                  fontSize="small"
                  sx={styles.listItemIconColor}
                />
              </ListItemIcon>
              <Typography variant="body2">{theme.attributes.name}</Typography>
            </ListItem>
          ))}
        </List>
      ) : (
        <Box sx={styles.noMatchBox}>
          <SearchOffIcon sx={styles.noMatchIcon} />
          <Typography variant="body2" color="text.secondary">
            No matching themes found
          </Typography>
        </Box>
      )}
    </>
  )
}

export default React.memo(MoveToThemeView)
